/**
 * Client-side CSV, for the one report the gateway has no export route for.
 *
 * The server renders spreadsheets for a department and for a person, but NOT for the
 * whole company — so a company-wide export is assembled here, from the report already
 * on screen. That turns out to be the better half of the deal anyway: it exports
 * exactly the rows the user is looking at, department filter and search included,
 * rather than re-running an unfiltered query server-side.
 *
 * Two things make a CSV actually open correctly in the spreadsheet these reports are
 * headed for:
 *
 *   - **A UTF-8 BOM.** Excel on Windows reads a BOM-less UTF-8 file as the system
 *     code page, which turns every Russian name into mojibake. The three bytes at the
 *     start are the entire fix, and they are why this builds a Blob rather than
 *     handing back a string.
 *   - **A separator hint.** Excel splits on the LIST separator of the user's locale,
 *     which in ru-RU is `;`, not `,` — a comma-separated file lands entirely in
 *     column A. `sep=;` on the first line is the one directive it honours, and the
 *     file stays valid CSV for everything else.
 */

const SEPARATOR = ';';

/** RFC 4180 quoting, plus the two characters that break Excel specifically. */
function cell(value: unknown): string {
  if (value == null) {
    return '';
  }
  const text = String(value);
  // A leading =, +, - or @ is executed as a formula by Excel and LibreOffice on open —
  // the CSV-injection class. A leading apostrophe forces it to text; a name that
  // genuinely starts with a minus does not exist in this data.
  const safe = /^[=+\-@]/.test(text) ? `'${text}` : text;
  return /["\n\r;,]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

export function buildCsv(
  headers: readonly string[],
  rows: readonly (readonly unknown[])[],
): Blob {
  const lines = [
    `sep=${SEPARATOR}`,
    headers.map(cell).join(SEPARATOR),
    ...rows.map(row => row.map(cell).join(SEPARATOR)),
  ];
  // \r\n, not \n: the line ending every spreadsheet on every platform accepts.
  return new Blob(['﻿' + lines.join('\r\n')], {
    type: 'text/csv;charset=utf-8',
  });
}

/**
 * A filename that survives being saved: no path separators, no characters Windows
 * refuses, and the period baked in so three exports of the same report do not
 * overwrite each other in the downloads folder.
 */
export function reportFileName(parts: readonly string[], extension: string): string {
  const stem = parts
    .filter(Boolean)
    .join(' — ')
    .replace(/[\\/:*?"<>|]/g, '-')
    .slice(0, 120);
  return `${stem}.${extension}`;
}
